import { faCalendarCheck, faSync } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Badge, Button, Input, Spinner, Table } from "reactstrap";
import { useDemoCore } from "../../../cores/demo";
import {
  DemoRequest,
  DemoStatus,
} from "../../../cores/demo/interfaces/DemoRequest";
import "../demo/DemoRequest.scss";

const STATUS_COLOR: Record<DemoStatus, string> = {
  pending: "warning",
  scheduled: "info",
  done: "success",
  cancelled: "secondary",
};

export function WembiiAdminDemoRequestsSection() {
  const intl = useIntl();
  const { getDemoRequests, updateDemoRequestStatus } = useDemoCore();
  const [requests, setRequests] = useState<DemoRequest[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const data = await getDemoRequests();
    setRequests(data);
    setLoading(false);
  }, [getDemoRequests]);

  useEffect(() => {
    load();
  }, [load]);

  const onStatusChange = useCallback(
    async (id: number, status: DemoStatus) => {
      const updated = await updateDemoRequestStatus(id, status);
      if (updated) {
        setRequests((prev) =>
          prev.map((r) => (r.id === id ? { ...r, status } : r))
        );
      }
    },
    [updateDemoRequestStatus]
  );

  const channelLabel = (channel: string) =>
    intl.formatMessage({ id: `app.demo.channel.${channel}` });

  return (
    <div className="mb-5">
      <h5 className="wembii-admin-section-title mb-2 d-flex align-items-center">
        <FontAwesomeIcon icon={faCalendarCheck} className="mr-2" />
        {intl.formatMessage({ id: "app.demo.admin.title" })}
        {requests.length > 0 && (
          <Badge color="primary" pill className="ml-2 align-middle">
            {requests.length}
          </Badge>
        )}
        <Button
          color="link"
          className="ml-2 p-0"
          onClick={load}
          title={intl.formatMessage({ id: "app.demo.admin.refresh" })}
        >
          <FontAwesomeIcon icon={faSync} spin={loading} />
        </Button>
      </h5>
      <p className="small text-muted mb-3">
        {intl.formatMessage({ id: "app.demo.admin.intro" })}
      </p>

      {loading && requests.length === 0 ? (
        <div className="text-center py-3">
          <Spinner size="sm" />
        </div>
      ) : requests.length === 0 ? (
        <p className="text-muted small">
          {intl.formatMessage({ id: "app.demo.admin.empty" })}
        </p>
      ) : (
        <div className="table-responsive">
          <Table hover size="sm" className="align-middle">
            <thead>
              <tr>
                <th>{intl.formatMessage({ id: "app.demo.admin.col.date" })}</th>
                <th>{intl.formatMessage({ id: "app.demo.admin.col.name" })}</th>
                <th>{intl.formatMessage({ id: "app.emailTxt" })}</th>
                <th>{intl.formatMessage({ id: "app.phoneTxt" })}</th>
                <th>
                  {intl.formatMessage({ id: "app.demo.form.preferredAt" })}
                </th>
                <th>{intl.formatMessage({ id: "app.demo.form.channel" })}</th>
                <th>{intl.formatMessage({ id: "app.demo.form.message" })}</th>
                <th>{intl.formatMessage({ id: "app.demo.admin.col.status" })}</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((r) => (
                <tr key={r.id}>
                  <td className="text-nowrap">
                    {moment(r.createdAt).format("DD/MM/YY HH:mm")}
                  </td>
                  <td>{r.name}</td>
                  <td>{r.email}</td>
                  <td className="text-nowrap">{r.phone}</td>
                  <td className="text-nowrap">{r.preferredAt}</td>
                  <td>{channelLabel(r.channel)}</td>
                  <td style={{ maxWidth: 240, whiteSpace: "pre-wrap" }}>
                    {r.message}
                  </td>
                  <td>
                    <Input
                      type="select"
                      bsSize="sm"
                      className="demo-admin__status-select"
                      value={r.status}
                      onChange={(e) =>
                        onStatusChange(r.id, e.target.value as DemoStatus)
                      }
                    >
                      <option value="pending">
                        {intl.formatMessage({ id: "app.demo.status.pending" })}
                      </option>
                      <option value="scheduled">
                        {intl.formatMessage({ id: "app.demo.status.scheduled" })}
                      </option>
                      <option value="done">
                        {intl.formatMessage({ id: "app.demo.status.done" })}
                      </option>
                      <option value="cancelled">
                        {intl.formatMessage({ id: "app.demo.status.cancelled" })}
                      </option>
                    </Input>
                    <Badge
                      color={STATUS_COLOR[r.status]}
                      pill
                      className="mt-1 d-inline-block"
                    >
                      {intl.formatMessage({ id: `app.demo.status.${r.status}` })}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
}
